import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { useIdempotencyKey, idempotencyHeader } from "@/lib/idempotency";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";

// Wraps the Stripe Elements provider once we have a client secret back from
// the backend. Split into two stages so we don't call Stripe.js until the
// user actually clicks "Subscribe" — avoids loading the Stripe SDK on every
// Settings render.
//
// v77.99.10 — Accepts `planInterval` ("monthly" | "yearly"); the backend
// resolves the right Stripe Price from this value. Defaults to monthly so
// any existing call site without the prop keeps working.
export const StripeCheckoutForm = ({ onSuccess, planInterval = "monthly" }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [err, setErr] = useState(null);
  const [initBusy, setInitBusy] = useState(false);
  // v77.99.18 — One idempotency key per checkout attempt. The backend
  // collapses replays so a double-click on "Subscribe" can't create
  // two Stripe subscriptions for the same user. The key is also
  // forwarded to Stripe's own `Idempotency-Key` API header so the
  // upstream `Subscription.create` collapses too.
  const { key: subscribeKey, rotate: rotateSubscribeKey } = useIdempotencyKey();

  const beginCheckout = async () => {
    setErr(null);
    setInitBusy(true);
    try {
      // The backend returns the publishable key along with the client secret
      // so we don't depend on the env var for bootstrapping.
      const { data } = await api.post(
        "/subscription/subscribe",
        { plan_interval: planInterval },
        { headers: idempotencyHeader(subscribeKey) },
      );
      setStripePromise(loadStripe(data.publishable_key));
      setClientSecret(data.client_secret);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Couldn't start checkout";
      setErr(typeof msg === "string" ? msg : "Couldn't start checkout");
      // Rotate the key on error so a real retry hits a fresh slot.
      // (Replay-of-success returns the cached body, so we wouldn't
      // land in the catch block on a legitimate replay.)
      rotateSubscribeKey();
    } finally {
      setInitBusy(false);
    }
  };

  if (err) {
    return (
      <div className="border border-[#E8B8B2] bg-[#FBEFED] text-[#8C2A22] rounded-md p-4" data-testid="stripe-init-error">
        <div className="font-medium text-sm">We couldn&apos;t start your checkout</div>
        <div className="text-xs mt-1">{err}</div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-3 rounded-sm"
          onClick={beginCheckout}
          data-testid="stripe-retry"
        >
          Try again
        </Button>
      </div>
    );
  }

  if (!clientSecret) {
    // v77.99.10 — Plan-aware "before you subscribe" copy. Reuses the
    // canonical pricing constants so changes flow from one place.
    const isYearly = planInterval === "yearly";
    return (
      <div className="space-y-3" data-testid="stripe-prepay-block">
        <div className="text-xs text-[#2E3A3F] bg-[#F4F8F9] border border-[#D9E1E3] rounded-sm p-3 leading-relaxed" data-testid="stripe-billing-summary">
          <div className="font-medium text-[#0B4D50] mb-1">Before you subscribe</div>
          {isYearly ? (
            <ul className="space-y-1 list-disc pl-5">
              <li>€99 charged once, up front, for a full year of access.</li>
              <li>Equivalent to €8.25/month — saves €32.88 vs. paying monthly for the same period.</li>
              <li>Cancel anytime — your access continues until the end of the year you have paid for.</li>
              <li>No partial refunds for unused time.</li>
            </ul>
          ) : (
            <ul className="space-y-1 list-disc pl-5">
              <li>€10.99/month, billed monthly to your card.</li>
              <li>Minimum 3-month commitment (€32.97 total minimum).</li>
              <li>Cancel anytime after month 3 — takes effect at the end of the current billing period.</li>
              <li>No partial refunds for unused time.</li>
            </ul>
          )}
        </div>
        <Button
          type="button"
          onClick={beginCheckout}
          disabled={initBusy}
          className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm px-6 py-5 text-sm h-auto"
          data-testid="upgrade-subscribe"
        >
          {initBusy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening payment form…
            </>
          ) : (
            <>{isYearly ? "Subscribe — €99/yr" : "Subscribe — €10.99/mo (3-month minimum)"}</>
          )}
        </Button>
      </div>
    );
  }

  const appearance = {
    theme: "stripe",
    variables: {
      colorPrimary: "#1F9FA4",
      colorText: "#2E3A3F",
      colorDanger: "#8C2A22",
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      borderRadius: "4px",
    },
  };

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <InnerCheckoutForm onSuccess={onSuccess} planInterval={planInterval} />
    </Elements>
  );
};

// Actual Stripe Elements form — confirms the PaymentIntent that the backend
// attached to the first invoice. Stripe handles 3-D Secure, saves the card
// as the default payment method (via `save_default_payment_method` in the
// Subscription request), and fires our webhook on success.
const InnerCheckoutForm = ({ onSuccess, planInterval = "monthly" }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    // The success redirect is never actually followed — we pass
    // `redirect: "if_required"` and handle the result inline. But Stripe
    // still insists on a return_url for fallback flows, so we give it one
    // that just brings the user back to this same page.
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/app/upgrade?confirmed=1`,
      },
      redirect: "if_required",
    });
    if (result.error) {
      toast.error(result.error.message || "Payment failed");
      setBusy(false);
      return;
    }
    // On success the webhook will flip the user's plan to `paid` server-side.
    // We show a happy-path state immediately so the user isn't staring at
    // a spinner; the next poll of /subscription/me (or a page refresh) will
    // confirm the new plan state.
    setDone(true);
    setBusy(false);
    toast.success("Payment successful! Welcome to Paid.");
    if (onSuccess) onSuccess();
  };

  if (done) {
    return (
      <div className="border border-[#A9D3A9] bg-[#DCEFDC] rounded-md p-6 text-center space-y-3" data-testid="stripe-payment-done">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white text-[#1E5A1E]">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <div className="font-heading text-xl text-[#1E5A1E]">You&apos;re on the Paid plan</div>
        <p className="text-sm text-[#1E5A1E]">We&apos;ll email your first receipt shortly. Your next billing date and payment counter appear on Settings.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="stripe-elements-form">
      <PaymentElement options={{ layout: "tabs" }} />
      <div className="flex items-center gap-2 text-xs text-[#6F828A]">
        <Lock className="w-3.5 h-3.5" /> Payment handled by Stripe. Your card details never touch our servers.
      </div>
      <Button
        type="submit"
        disabled={!stripe || busy}
        className="w-full sm:w-auto bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm px-6 py-5 text-sm h-auto"
        data-testid="stripe-pay-button"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…
          </>
        ) : (
          <>{planInterval === "yearly" ? "Pay €99 and start Paid plan" : "Pay €10.99 and start Paid plan"}</>
        )}
      </Button>
    </form>
  );
};
